<template>
    <div class="rotating-words band-night">
        <h1>
            {{ data?.content?.static }}
            <span class="words">
                <span
                    v-for="dynamic in data?.content?.dynamic"
                    v-bind:key="dynamic.id"
                >
                    <span v-if="dynamic?.space">&nbsp;</span>
                    <span v-else></span>
                    <span>{{ dynamic?.word }}</span>
                    <span>.</span>
                </span>
            </span>
        </h1>
        <!-- Animated heartbeat above the store buttons. Same ECG
             component used by the Home teaser and Features deep-dive,
             so the brand cue carries through the closing CTA: the
             metaphor of "we listen for your heartbeat" lands one last
             time right before the user decides to install. Reuses the
             shared SVG so the pulse stays in lockstep across surfaces.
             Conditional on the same v-if as the buttons so non-CTA
             pages (FAQ, Privacy, etc.) don't see an orphan pulse. -->
        <HeartbeatPulse
            v-if="data?.buttons"
            class="rotating-words__ecg"
        />
        <div
            v-if="data?.buttons"
            class="mobile-stores-teaser"
        >
            <Link
                v-for="button in resolvedButtons"
                :to="button.url"
                :label="button.label"
                :style="button.style"
                :modal="button.modal"
                :image="button.image"
                v-bind:key="button.id"
            />
        </div>
        <!-- Subtitle that follows the download buttons. Hard-coded
             (not a Strapi field) because it's tied to a brand
             decision rather than CMS content. Locale-aware via the
             store. Conditional on the same v-if as the buttons so
             pages without download buttons (FAQ, Privacy, etc.)
             don't see an orphan line. -->
        <p
            v-if="data?.buttons"
            class="mobile-stores-teaser__subtitle"
        >
            {{ subtitleText }}
        </p>
    </div>
</template>

<script>
import { mapState } from 'pinia'
import { useStore } from '@/stores/index'
import Link from '@/components/Links/Link.vue'
import HeartbeatPulse from '@/components/Sections/HeartbeatPulse.vue'

const SUBTITLES = {
    'en-US': 'This service is built for your phone. For now.',
    'fr-FR': "Ce service est conçu pour votre téléphone. Pour l'instant.",
}

/* Canonical store URLs. We override whatever Strapi returns for the
   store buttons so the live links don't depend on a CMS edit being
   remembered. Strapi still owns the label, the image, and the order
   of the buttons ; only the URL is force-replaced when we recognize
   one of these hosts (or a store-image filename) on the button. */
const IOS_STORE_URL = 'https://apps.apple.com/app/bzioup/id6758737021'
const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.bzioup.app'

function detectStore(button) {
    const url = (button?.url || '').toLowerCase()
    // image can be a string (filename) or a Strapi media object ; cover both.
    const imageHint = (
        typeof button?.image === 'string'
            ? button.image
            : (button?.image?.url || button?.image?.name || '')
    ).toLowerCase()
    const haystack = `${url} ${imageHint}`
    if (haystack.includes('apple') || haystack.includes('appstore') || haystack.includes('app-store') || haystack.includes('ios')) {
        return 'ios'
    }
    if (haystack.includes('google') || haystack.includes('playstore') || haystack.includes('play-store') || haystack.includes('android')) {
        return 'android'
    }
    return null
}

export default {
    name: 'RotatingWordsText',
    components: {
        Link,
        HeartbeatPulse,
    },
    props: {
        data: Object
    },
    computed: {
        ...mapState(useStore, ['language']),
        subtitleText() {
            return SUBTITLES[this.language] || SUBTITLES['en-US']
        },
        /* Force-override the store URLs returned by Strapi. Detection
           uses both the existing URL and the image filename so a
           button is still recognized even if the CMS URL is empty or
           wrong. Non-store buttons (anything we don't recognize) pass
           through untouched. */
        resolvedButtons() {
            const buttons = this.data?.buttons
            if (!Array.isArray(buttons)) return buttons
            return buttons.map(button => {
                const store = detectStore(button)
                if (store === 'ios') return { ...button, url: IOS_STORE_URL }
                if (store === 'android') return { ...button, url: ANDROID_STORE_URL }
                return button
            })
        },
    },
}
</script>

<style lang="scss">
.rotating-words {
    position: relative;
    overflow: hidden;
    text-align: center;
    color: #fff;
    background-color: var(--bzioup-color-night);
    background-image:
        radial-gradient(ellipse 55% 60% at 50% 110%, rgba(218, 138, 139, 0.3), transparent 70%);
    padding-top: max(56px, 6vw) !important;
    padding-bottom: max(56px, 6vw) !important;

    > h1 {
        margin: 0 auto 36px;
        max-width: 900px;
        font-size: clamp(34px, 5.2vw, 64px);
        line-height: 1.15;
    }

    > div {
        text-align: center;
    }
}

.rotating-words .rotating-words__ecg {
    width: 180px;
    height: 44px;
    margin: 0 auto 32px;
    display: block;
}

.rotating-words .mobile-stores-teaser {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;

    a {
        display: inline-block;
        line-height: 0;
        margin: 0;
        transition: transform 180ms cubic-bezier(0.2, 0.8, 0.3, 1);
    }

    a:hover {
        transform: translateY(-2px) scale(1.02);
    }

    a img {
        height: 52px;
        width: auto;
    }
}

.mobile-stores-teaser__subtitle {
    text-align: center;
    margin: 18px 0 0;
    font-size: 14px;
    font-style: italic;
    color: #fff;
    opacity: 0.55;
}

/* All words stack in the same grid cell, so the container is exactly
   as wide as the longest one and the headline stays centered. */
.words {
    display: inline-grid;
    text-align: left;
    vertical-align: top;

    > span {
        grid-area: 1 / 1;
        opacity: 0;
        color: var(--bzioup-color-pink);
        animation: topToBottom 12.5s linear infinite 0s;
    }

    > span > span:nth-child(2) {
        text-decoration: underline;
        text-decoration-thickness: 4px;
        text-underline-offset: 6px;
        text-decoration-color: rgba(218, 138, 139, 0.6);
    }
}

.words > span:nth-child(2) { animation-delay: 2.5s; }
.words > span:nth-child(3) { animation-delay: 5s; }
.words > span:nth-child(4) { animation-delay: 7.5s; }
.words > span:nth-child(5) { animation-delay: 10s; }

@keyframes topToBottom {
    0%   { opacity: 0; }
    5%   { opacity: 0; transform: translateY(-40px); }
    10%  { opacity: 1; transform: translateY(0); }
    25%  { opacity: 1; transform: translateY(0); }
    30%  { opacity: 0; transform: translateY(40px); }
    80%  { opacity: 0; }
    100% { opacity: 0; }
}

/* Phones: the rotating word takes its own line under the static text. */
@media (max-width: $mq-medium - 1) {
    .words {
        display: grid;
        justify-items: center;
        text-align: center;
        margin-top: 4px;
    }
}

@media (max-width: $mq-small) {
    .rotating-words .mobile-stores-teaser a img {
        height: 42px;
    }
}
</style>
